import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoader } from '../components/shared/LoadingSpinner';
import { EmptyState } from '../components/shared/EmptyState';
import { StatusBadge } from '../components/shared/StatusBadge';
import { formatDateTime } from '../lib/utils';
import { Plug, Plus, Trash2, Play, RefreshCw, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';

const CONNECTOR_TYPES = [
  { 
    value: 'aws', 
    label: 'Amazon Web Services', 
    icon: '☁️', 
    description: 'S3, IAM, CloudTrail, VPC',
    fields: [
      { key: 'access_key_id', label: 'Access Key ID', placeholder: 'AKIAIOSFODNN7EXAMPLE', type: 'text' },
      { key: 'secret_access_key', label: 'Secret Access Key', placeholder: 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY', type: 'password' },
      { key: 'region', label: 'Region', placeholder: 'us-east-1', type: 'text' },
    ]
  },
  { 
    value: 'github', 
    label: 'GitHub', 
    icon: '🐙', 
    description: 'Branch Protection, Code Scanning',
    fields: [
      { key: 'token', label: 'Personal Access Token', placeholder: 'ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx', type: 'password' },
      { key: 'organization', label: 'Organization (optional)', placeholder: 'your-org', type: 'text' },
    ]
  },
  { 
    value: 'okta', 
    label: 'Okta', 
    icon: '🔐', 
    description: 'MFA, SSO, User Provisioning',
    fields: [
      { key: 'domain', label: 'Okta Domain', placeholder: 'your-org.okta.com', type: 'text' },
      { key: 'api_token', label: 'API Token', placeholder: 'your-okta-api-token', type: 'password' },
    ]
  },
];

export default function ConnectorsPage() {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [scanning, setScanning] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'aws',
    config: {},
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchConnectors();
  }, []);

  const fetchConnectors = async () => {
    try {
      const response = await api.get('/connectors');
      setConnectors(response.data);
    } catch (error) {
      toast.error('Failed to load connectors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post('/connectors', formData);
      toast.success('Connector added successfully');
      setIsDialogOpen(false);
      setFormData({ name: '', type: 'aws', config: {} });
      fetchConnectors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add connector');
    } finally {
      setSubmitting(false);
    }
  };

  const handleScan = async (connectorId) => {
    setScanning(connectorId);
    try {
      const response = await api.post(`/connectors/${connectorId}/scan`);
      setScanResults(response.data);
      toast.success(`Scan complete: ${response.data.summary.compliant} compliant, ${response.data.summary.gap} gaps`);
      fetchConnectors();
    } catch (error) {
      toast.error('Scan failed');
    } finally {
      setScanning(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this connector?')) return;
    
    try {
      await api.delete(`/connectors/${id}`);
      toast.success('Connector removed');
      fetchConnectors();
    } catch (error) {
      toast.error('Failed to remove connector');
    }
  };

  const getConnectorIcon = (type) => {
    const connector = CONNECTOR_TYPES.find(c => c.value === type);
    return connector?.icon || '🔌';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <PageLoader />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader 
        title="Integrations" 
        description="Connect your security tools for automated evidence collection"
        actions={
          <Button
            data-testid="add-connector-btn"
            onClick={() => setIsDialogOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
          >
            <Plus size={18} className="mr-2" />
            Add Connector
          </Button>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 md:px-8 lg:px-12">
        {/* Available Connectors */}
        <div className="mb-8">
          <h3 className="font-secondary font-semibold text-lg text-slate-900 mb-4">
            Available Integrations
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {CONNECTOR_TYPES.map((type) => (
              <div 
                key={type.value}
                className="bg-white border border-slate-200 rounded-sm p-4 hover:border-blue-300 transition-colors cursor-pointer"
                onClick={() => {
                  setFormData({ ...formData, type: type.value, name: type.label });
                  setIsDialogOpen(true);
                }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{type.icon}</span>
                  <h4 className="font-semibold text-slate-900">{type.label}</h4>
                </div>
                <p className="text-sm text-slate-500">{type.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Active Connectors */}
        <div>
          <h3 className="font-secondary font-semibold text-lg text-slate-900 mb-4">
            Active Connectors ({connectors.length})
          </h3>
          
          {connectors.length > 0 ? (
            <div className="space-y-4">
              {connectors.map((connector) => (
                <div 
                  key={connector.id}
                  className="bg-white border border-slate-200 rounded-sm p-5"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-3xl">{getConnectorIcon(connector.type)}</span>
                      <div>
                        <h4 className="font-semibold text-slate-900">{connector.name}</h4>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="badge badge-neutral uppercase text-xs">{connector.type}</span>
                          <StatusBadge status={connector.status === 'connected' ? 'compliant' : 'pending'} />
                          {connector.last_sync && (
                            <span className="text-xs text-slate-400">
                              Last sync: {formatDateTime(connector.last_sync)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        data-testid={`scan-connector-${connector.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleScan(connector.id)}
                        disabled={scanning === connector.id}
                        className="rounded-sm"
                      >
                        {scanning === connector.id ? (
                          <RefreshCw size={16} className="mr-2 animate-spin" />
                        ) : (
                          <Play size={16} className="mr-2" />
                        )}
                        {scanning === connector.id ? 'Scanning...' : 'Run Scan'}
                      </Button>
                      <Button
                        data-testid={`delete-connector-${connector.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(connector.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Plug}
              title="No connectors configured"
              description="Add a connector to automatically collect compliance evidence from your infrastructure."
              action={
                <Button
                  data-testid="empty-add-connector-btn"
                  onClick={() => setIsDialogOpen(true)}
                  className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                >
                  <Plus size={18} className="mr-2" />
                  Add Your First Connector
                </Button>
              }
            />
          )}
        </div>
      </div>

      {/* Add Connector Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-secondary">Add Connector</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Connector Name</Label>
              <Input
                data-testid="connector-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Production AWS Account"
                required
                className="mt-1.5 rounded-sm"
              />
            </div>

            <div>
              <Label>Type</Label>
              <Select 
                value={formData.type} 
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger data-testid="connector-type-select" className="mt-1.5 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONNECTOR_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center gap-2">
                        <span>{type.icon}</span>
                        <span>{type.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dynamic Credential Fields */}
            <div className="space-y-3">
              {CONNECTOR_TYPES.find(c => c.value === formData.type)?.fields?.map((field) => (
                <div key={field.key}>
                  <Label>{field.label}</Label>
                  <Input
                    data-testid={`connector-${field.key}-input`}
                    type={field.type}
                    value={formData.config[field.key] || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      config: { ...formData.config, [field.key]: e.target.value }
                    })}
                    placeholder={field.placeholder}
                    className="mt-1.5 rounded-sm font-mono text-sm"
                  />
                </div>
              ))}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
              <p className="text-sm text-amber-700">
                <strong>Note:</strong> Credentials are stored securely and used only for compliance scans.
                Leave blank for demo mode with simulated results.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-connector-btn"
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              >
                {submitting ? 'Adding...' : 'Add Connector'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Scan Results Dialog */}
      <Dialog open={!!scanResults} onOpenChange={() => setScanResults(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary">
              Scan Results - {scanResults?.connector_type?.toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          {scanResults && (
            <div className="mt-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-center">
                  <CheckCircle size={24} className="mx-auto text-emerald-600 mb-2" />
                  <p className="text-2xl font-bold text-emerald-700">{scanResults.summary.compliant}</p>
                  <p className="text-xs text-emerald-600">Compliant</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-sm p-4 text-center">
                  <XCircle size={24} className="mx-auto text-red-600 mb-2" />
                  <p className="text-2xl font-bold text-red-700">{scanResults.summary.gap}</p>
                  <p className="text-xs text-red-600">Gaps</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-center">
                  <AlertTriangle size={24} className="mx-auto text-slate-500 mb-2" />
                  <p className="text-2xl font-bold text-slate-700">{scanResults.summary.pending}</p>
                  <p className="text-xs text-slate-500">Pending</p>
                </div>
              </div>

              {/* Checks */}
              <h4 className="font-semibold text-slate-900 mb-3">Checks Performed</h4>
              <div className="space-y-2">
                {scanResults.checks.map((check, index) => (
                  <div 
                    key={index}
                    className={`p-3 rounded-sm border ${
                      check.status === 'compliant' 
                        ? 'bg-emerald-50 border-emerald-200'
                        : check.status === 'gap'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{check.check}</p>
                        <p className="text-xs text-slate-500">{check.resource}</p>
                      </div>
                      <div className="text-right">
                        <StatusBadge status={check.status} />
                        <p className="text-xs text-slate-500 mt-1">{check.details}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-slate-400 mt-4">
                Scanned at: {formatDateTime(scanResults.scanned_at)}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
